import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import PageHero, { SectionHeading } from "../components/PageHero";
import AltarSection from "../components/AltarSection";

// ---------------------------------------------------------------------------
// Tuesday schedule (order is meaningful — read top-to-bottom as a day rhythm)
// ---------------------------------------------------------------------------
const tuesdaySchedule = [
  {
    title: "Morning Chapter",
    body: "Knights of St. Louis and Ladies of St. Elizabeth gather separately for prayer, formation, friendship, mentorship, and service.",
  },
  {
    title: "Catechesis of the Good Shepherd & Applied Learning",
    groups: [
      {
        label: "Catechesis of the Good Shepherd",
        items: ["Level I", "Level II", "Level III"],
      },
      {
        label: "Applied Learning",
        items: ["Trades", "Lost Arts", "Handicrafts", "Homesteading"],
      },
    ],
  },
  {
    title: "Holy Mass",
    body: "Students attend the Holy Sacrifice of the Mass together.",
  },
  {
    title: "Physical Education",
    body: "A shared period devoted to physical activity, games, movement, and recreation.",
  },
  {
    title: "Music",
    body: "Individual and small-group music instruction may include:",
    items: ["Piano", "Suzuki Violin", "Voice", "Flute"],
  },
  {
    title: "Club Period / Study Hall",
    body: "A flexible period for clubs, independent reading, study, and completion of schoolwork.",
  },
];

// ---------------------------------------------------------------------------
// Thursday electives (offerings vary by age and year)
// ---------------------------------------------------------------------------
const thursdayCategories = [
  {
    title: "Theology",
    items: [
      "Letters, Saints, & Virtues",
      "The Story of the Bible: The Old Testament",
      "The Bible, Saints, and Catechism",
      "Holy Scripture",
    ],
  },
  {
    title: "Fine Arts & Music",
    items: [
      "Artistic Compositions",
      "Art",
      "Drawing",
      "Song Sampler",
      "Music Menagerie",
      "Musical Theater",
      "Choir",
    ],
  },
  {
    title: "Languages",
    items: ["Creative Spanish"],
  },
  {
    title: "Speech, Rhetoric & Performance",
    items: [
      "Junior Speech & Debate",
      "Competitive Speech and Debate",
    ],
  },
  {
    title: "Practical & Applied Arts",
    items: ["Machine Sewing", "Homemade Essentials"],
  },
  {
    title: "Physical Education & Dance",
    items: ["Ballet", "Ballroom Dancing", "PE & Outdoor Games"],
  },
  {
    title: "STEM & Applied Sciences",
    items: [
      "Robotics",
      "Our Human Body",
    ],
  },
  {
    title: "Intellectual Enrichment",
    items: ["Chess", "Math Explorers", "Math Is Beautiful!", "Geography, Culture & Faith"],
  },
];

// ---------------------------------------------------------------------------
// Small presentational atoms local to this page
// ---------------------------------------------------------------------------
const ScheduleBlock = ({ title, body, groups, items }) => (
  <div className="py-8 md:py-10 border-b border-[#eae1c4] last:border-b-0">
    <h3
      className="font-display text-[#1e2952] text-[22px] md:text-[26px] leading-tight"
      style={{ fontWeight: 600 }}
    >
      {title}
    </h3>
    <div className="h-px w-10 bg-[#c9a961] mt-3 mb-5"></div>

    {body && (
      <p className="font-serif-body text-[16px] md:text-[16.5px] leading-[1.75] text-[#3a466e]">
        {body}
      </p>
    )}

    {items && (
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5 font-serif-body text-[15.5px] text-[#3a466e]">
        {items.map((it) => (
          <li key={it} className="before:content-['·'] before:text-[#c9a961] before:mr-2">
            {it}
          </li>
        ))}
      </ul>
    )}

    {groups && (
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
        {groups.map((g) => (
          <div key={g.label}>
            <div className="font-display italic text-[#1e2952] text-[18px] mb-3" style={{ fontWeight: 500 }}>
              {g.label}
            </div>
            <ul className="font-serif-body text-[15.5px] leading-[1.9] text-[#3a466e] space-y-0.5">
              {g.items.map((it) => (
                <li key={it} className="before:content-['·'] before:text-[#c9a961] before:mr-2">
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ElectiveCategory = ({ title, items }) => (
  <div className="py-6 md:py-8 border-b border-[#eae1c4] last:border-b-0">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
      <div className="md:col-span-4">
        <h3
          className="font-display text-[#1e2952] text-[20px] md:text-[22px] leading-tight"
          style={{ fontWeight: 600 }}
        >
          {title}
        </h3>
        <div className="h-px w-8 bg-[#c9a961] mt-2"></div>
      </div>
      <ul className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 font-serif-body text-[15.5px] leading-[1.85] text-[#3a466e]">
        {items.map((it) => {
          const isNested = typeof it === "object" && it !== null;
          if (isNested) {
            return (
              <li key={it.label} className="before:content-['·'] before:text-[#c9a961] before:mr-2">
                {it.label}
                {it.subitems && it.subitems.length > 0 && (
                  <ul className="mt-1 ml-4 space-y-0.5 font-display italic text-[14.5px] text-[#8a7a4e]">
                    {it.subitems.map((s) => (
                      <li key={s} className="before:content-['—'] before:text-[#c9a961]/70 before:mr-2 before:not-italic">
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          }
          return (
            <li key={it} className="before:content-['·'] before:text-[#c9a961] before:mr-2">
              {it}
            </li>
          );
        })}
      </ul>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const Electives = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t = setTimeout(scroll, 60);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <PageLayout>
      <PageHero
        eyebrow="Electives & Faith Formation"
        headline={
          <>
            The Faith Lived.{" "}
            <em className="not-italic text-[#c9a961]">The Faith Loved.</em>
          </>
        }
      />

      {/* INTRO — Program overview */}
      <section className="bg-[#fbf7ee]" data-testid="electives-leisure-intro">
        <div className="max-w-[880px] mx-auto px-6 md:px-10 lg:px-14 pt-2 md:pt-3 pb-2">
          <p className="font-serif-body text-[17px] md:text-[18px] leading-[1.9] text-[#3a466e]">
            In conjunction with the Our Lady of Fatima and Children of Fatima Co-Ops, Angel of Peace Academy will offer an extensive array of faith formation, enrichment, and elective opportunities. Students may participate in the full two-day program, or families may register à la carte for individual classes directly through the Our Lady of Fatima and Children of Fatima Co-Ops.
          </p>
        </div>
      </section>

      {/* The Orders — single-sex formation */}
      <section
        id="orders"
        className="bg-[#f6efd8]/40 border-y border-[#eae1c4] scroll-mt-24 mt-6 md:mt-8"
        data-testid="electives-orders-section"
      >
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24">
          <SectionHeading
            eyebrow="Formation"
            title="The Orders: Single-Sex Formation for Boys and Girls."
          />

          <div className="max-w-[880px] mx-auto space-y-5 font-serif-body text-[16.5px] md:text-[17px] leading-[1.85] text-[#3a466e]">
            <p>
              Every Tuesday and Thursday begins with <span className="font-display italic text-[#1e2952]">Morning Chapter</span>,
              a time set apart for friendship, mentorship, virtue, and formation in the Catholic faith. Boys and girls
              meet separately: the <span className="font-display italic text-[#1e2952]">Order of St. Louis IX</span>
              {" "}for boys and the <span className="font-display italic text-[#1e2952]">Order of St. Elizabeth of Hungary</span>{" "}
              for girls.
            </p>
            <p>
              Boys and girls possess equal dignity before God, but their formation need not be identical. The Orders offer
              guidance attentive to the particular strengths, challenges, responsibilities, and vocations of men and women,
              while remaining united in the same Catholic faith and the same call to holiness.
            </p>
          </div>

          {/* Two Orders — side by side on desktop, stacked on mobile */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-16 lg:gap-20 items-start max-w-[1180px] mx-auto">
            {/* Order of St. Louis IX */}
            <div className="flex flex-col items-center text-center" data-testid="order-st-louis">
              <img
                src="/assets/order-st-louis-ix.png"
                alt="Order of St. Louis IX crest"
                className="block w-auto"
                style={{ height: "min(96vw, 400px)", maxWidth: "100%" }}
              />
              <h3
                className="mt-8 font-display text-[#1e2952] text-[19px] sm:text-[24px] md:text-[26px] lg:text-[30px] leading-tight"
                style={{ fontWeight: 500 }}
              >
                The Order of St. Louis IX
              </h3>
              <div className="mt-3 h-px w-12 bg-[#c9a961]"></div>
              <div className="mt-3 font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961]">
                Knights
              </div>
              <div className="mt-6 space-y-4 font-serif-body text-[16px] leading-[1.85] text-[#3a466e] text-left max-w-[440px]">
                <p>
                  St. Louis IX, King of France, gives the boys a model of strength placed at the service of something
                  greater than oneself. His kingship was marked by prayer, justice, sacrifice, leadership, service to the
                  poor, and fidelity to the Church.
                </p>
                <p>
                  Formation within the Order encourages boys toward prayer and altar service, self-command, moral courage,
                  defense of the Faith, works of mercy, leadership, and friendship. As they mature, boys are also given
                  opportunities to encounter and discern the vocations through which a man may give his life in service
                  to God, his family, the Church, and the world.
                </p>
              </div>
            </div>

            {/* Order of St. Elizabeth of Hungary */}
            <div className="flex flex-col items-center text-center" data-testid="order-st-elizabeth">
              <img
                src="/assets/order-st-elizabeth.png"
                alt="Order of St. Elizabeth of Hungary crest"
                className="block w-auto"
                style={{ height: "min(96vw, 400px)", maxWidth: "100%" }}
              />
              <h3
                className="mt-8 font-display text-[#1e2952] text-[19px] sm:text-[24px] md:text-[26px] lg:text-[30px] leading-tight"
                style={{ fontWeight: 500 }}
              >
                The Order of St. Elizabeth of Hungary
              </h3>
              <div className="mt-3 h-px w-12 bg-[#c9a961]"></div>
              <div className="mt-3 font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961]">
                Ladies
              </div>
              <div className="mt-6 space-y-4 font-serif-body text-[16px] leading-[1.85] text-[#3a466e] text-left max-w-[440px]">
                <p>
                  St. Elizabeth of Hungary gives the girls a model of privilege, influence, and strength poured out in
                  charity. As a princess, wife, mother, and widow, she placed the gifts entrusted to her at the service
                  of Christ, the poor, and the suffering.
                </p>
                <p>
                  Formation within the Order encourages girls toward prayer and Marian devotion, works of mercy, gracious
                  strength, steadfast service, friendship, and growth in virtue. As they mature, girls are also given
                  opportunities to encounter and discern the vocations through which a woman may give her life in love
                  and service to God, her family, the Church, and the world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Altar illustration */}
      <AltarSection />

      {/* TUESDAYS */}
      <section className="bg-[#fbf7ee]" data-testid="electives-tuesdays-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20">
          <SectionHeading eyebrow="Tuesdays" title="Let them Leisure." />

          <p className="font-serif-body text-[#3a466e] text-[16.5px] md:text-[17px] leading-[1.8] max-w-[820px] mx-auto text-center mb-12 md:mb-14">
            Tuesdays begin with <span className="font-display italic text-[#1e2952]">Morning Chapter</span>, Angel of Peace' single-sex formation and mentorship program. The rest of the day provides students with a variety of faith, music, applied arts, and movement opportunities through the Children of Fatima Co-Op. Students pause midday for Mass and the day culminates with a community Rosary and a club period or supervised study hall.
          </p>

          <div className="max-w-[880px] mx-auto">
            {tuesdaySchedule.map((block) => (
              <ScheduleBlock key={block.title} {...block} />
            ))}
          </div>

          {/* À la carte note */}
          <div
            className="max-w-[880px] mx-auto mt-4 border-l-2 border-[#c9a961] pl-5 md:pl-6"
            data-testid="electives-alacarte-note-tuesday"
          >
            <p className="font-serif-body italic text-[15.5px] md:text-[16px] leading-[1.75] text-[#8a7a4e]">
              À la carte classes on Tuesdays are open for registration directly through the Children of Fatima Co-Op.
            </p>
          </div>
        </div>
      </section>

      {/* THURSDAYS */}
      <section
        className="bg-[#f6efd8]/40 border-y border-[#eae1c4]"
        data-testid="electives-thursdays-section"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20">
          <div className="text-center mb-10 md:mb-12">
            <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">
              Thursdays
            </div>
            <h2
              className="font-display text-[#1e2952] text-[32px] md:text-[40px] lg:text-[44px] leading-tight"
              style={{ fontWeight: 500 }}
            >
              (Re)form the Culture.
            </h2>
            <div className="mt-5 flex items-center justify-center gap-3">
              <span className="block h-px w-40 bg-[#c9a961]"></span>
              <span className="text-[#c9a961] text-sm">†</span>
              <span className="block h-px w-40 bg-[#c9a961]"></span>
            </div>
            <p className="font-serif-body mt-8 text-[#3a466e] text-[16.5px] md:text-[17px] leading-[1.8] max-w-[820px] mx-auto">
              Like Tuesdays, Thursdays begin with <span className="font-display italic text-[#1e2952]">Morning Chapter</span>. The rest of the day provides students with an extensive selection of electives through the Our Lady of Fatima Co-Op, pauses midday for Mass, and culminates with a community Rosary and a club period or supervised study hall.
            </p>
          </div>

          <div className="max-w-[1000px] mx-auto">
            {thursdayCategories.map((cat) => (
              <ElectiveCategory key={cat.title} {...cat} />
            ))}
          </div>

          {/* À la carte note */}
          <div
            className="max-w-[880px] mx-auto mt-4 border-l-2 border-[#c9a961] pl-5 md:pl-6"
            data-testid="electives-alacarte-note-thursday"
          >
            <p className="font-serif-body italic text-[15.5px] md:text-[16px] leading-[1.75] text-[#8a7a4e]">
              À la carte classes on Thursdays are open for registration directly through the Our Lady of Fatima Co-Op.
            </p>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="bg-[#fbf7ee]" data-testid="electives-location-section">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20 text-center">
          <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">
            Location
          </div>
          <h3
            className="font-display text-[#1e2952] text-[28px] md:text-[34px] leading-tight"
            style={{ fontWeight: 500 }}
          >
            Mater Dei Catholic Church
          </h3>
          <div className="font-serif-body italic text-[#8a7a4e] text-[16.5px] mt-2">
            Irving, Texas
          </div>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="block h-px w-40 bg-[#c9a961]"></span>
            <span className="text-[#c9a961] text-sm">†</span>
            <span className="block h-px w-40 bg-[#c9a961]"></span>
          </div>
          <p className="font-serif-body mt-8 text-[#3a466e] text-[16px] md:text-[16.5px] leading-[1.8] max-w-[720px] mx-auto">
            Tuesday and Thursday classes will be held at Mater Dei Catholic Church in Irving.
          </p>
        </div>
      </section>

      {/* Explore School Life link box */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 pt-2 pb-14 text-center">
          <Link
            to="/school-life"
            data-testid="electives-cta-schoollife"
            className="inline-block font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 border border-[#c9a961] text-[#1e2952] hover:bg-[#c9a961] hover:text-[#fbf7ee] transition-colors"
          >
            Explore School Life
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1e2952] text-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20 text-center">
          <h2
            className="font-display text-[26px] md:text-[36px] leading-tight"
            style={{ fontWeight: 500 }}
          >
            Discover Whether Angel of Peace Academy Is Right for Your Family.
          </h2>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="block h-px w-40 bg-[#c9a961]"></span>
            <span className="text-[#c9a961]">†</span>
            <span className="block h-px w-40 bg-[#c9a961]"></span>
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to="/inquire"
              data-testid="electives-cta-inquire"
              className="inline-block font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 bg-[#c9a961] text-[#1e2952] hover:bg-[#e2c98a] transition-colors"
            >
              Inquire Today
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Electives;
